import { queryOptions } from "@tanstack/react-query";
import { getOfficers } from "@/features/users/api/officersApi";
import {
  fetchStationRoster,
  fetchStationSeats,
} from "../../../shared/utils/fetchUsers";
import type { StationRoster, StationSeats } from "../../../shared/utils/fetchUsers";
import type { ListUsersResponse } from "../../../shared/interfaces/ListUsersResponse";

export const usersKeys = {
  all: ["users"] as const,
  station: (stationId: string) => [...usersKeys.all, "station", stationId] as const,
  stationList: (stationId: string, opts: { includeInactive: boolean }) =>
    [...usersKeys.station(stationId), opts] as const,
  stationSeats: (stationId: string) =>
    [...usersKeys.station(stationId), "seats"] as const,
};

/**
 * Active-officer directory used for synchronous name resolution
 * (userDirectory.getUserName). Mirrors the old getOfficers call — no
 * includeInactive param.
 */
export function stationDirectoryQuery(stationId: string) {
  return queryOptions<ListUsersResponse>({
    queryKey: usersKeys.stationList(stationId, { includeInactive: false }),
    queryFn: () => getOfficers(stationId),
    staleTime: 5 * 60_000,
    // Long retention: comments render names long after the fetch; an active
    // useUserDirectory observer keeps it alive while any consumer is mounted.
    gcTime: 30 * 60_000,
  });
}

/**
 * Full station roster (incl. inactive) for the ManageUsers panel, plus
 * lookup-failure info. userDirectory.getUserName scans this cache entry via
 * its `.users` property — keep that shape.
 */
export function stationUsersQuery(stationId: string) {
  return queryOptions<StationRoster>({
    queryKey: usersKeys.stationList(stationId, { includeInactive: true }),
    queryFn: () => fetchStationRoster(stationId, true),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/**
 * Seat usage for the ManageUsers panel. A separate endpoint (users:admin
 * only) so the third-party-consumed users list keeps a stable contract; the
 * key nests under usersKeys.station so roster invalidation refreshes both.
 */
export function stationSeatsQuery(stationId: string) {
  return queryOptions<StationSeats | null>({
    queryKey: usersKeys.stationSeats(stationId),
    queryFn: () => fetchStationSeats(stationId),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}
